import { cloneDeep } from 'lodash'

/**
 * @typedef {import('./schemas/2026.7.4').PlottrFile} PlottrFile_2026_7_4
 * @typedef {import('./schemas/2026.7.4').CollaboratorUIState} CollaboratorUIState_2026_7_4
 * @typedef {import('./schemas/2026.7.4').OutlineTabUi} OutlineTab_2026_7_4
 * @typedef {import('./schemas/2026.4.13').PlottrFile} PlottrFile_2026_4_13
 * @typedef {import('./schemas/2026.4.13').CollaboratorUIState} CollaboratorUIState_2026_4_13
 * @typedef {import('./schemas/2026.4.13').SearchAndFilterModal} SearchAndFilterModal_2026_4_13
 * @typedef {import('./schemas/2026.4.13').OutlineTabUi} OutlineTab_2026_4_13
 * @typedef {import('./schemas/2026.4.13').CharactersPlottrFileEntry} CharactersPlottrFileEntry_2026_4_13
 * @typedef {import('./schemas/2026.4.13').NotesPlottrFileEntry} NotesPlottrFileEntry_2026_4_13
 * @typedef {import('./schemas/2026.4.13').PlacesPlottrFileEntry} PlacesPlottrFileEntry_2026_4_13
 */

/**
 * @typedef {CharactersPlottrFileEntry_2026_4_13 | NotesPlottrFileEntry_2026_4_13 | PlacesPlottrFileEntry_2026_4_13} OldNotePlaceOrCharacter
 */

/**
 * @param {PlottrFile_2026_4_13['ui']['collaborators']} data
 * @return {boolean}
 */
function shouldAddMissingStateToCollaboratorsOrViewers(data) {
  /**
   * @param {CollaboratorUIState_2026_4_13} clonedUIState
   * @return {boolean}
   */
  const shouldAddMissingStateToClonedUiState = (clonedUIState) => {
    return (
      typeof clonedUIState.noteTab.showAsList !== 'boolean' ||
      typeof clonedUIState.noteTab.collapsedCategories !== 'boolean' ||
      typeof clonedUIState.characterTab.showAsList !== 'boolean' ||
      typeof clonedUIState.characterTab.collapsedCategories !== 'boolean' ||
      typeof clonedUIState.placeTab.showAsList !== 'boolean' ||
      typeof clonedUIState.placeTab.collapsedCategories !== 'boolean' ||
      (typeof clonedUIState.theme !== 'string' && clonedUIState.theme !== null) ||
      !clonedUIState.mobileControlsModal ||
      !clonedUIState.outlineTab.layout.fulltext.hoverDetails ||
      typeof clonedUIState.outlineTab.filterVisible !== 'boolean' ||
      typeof clonedUIState.timeline.filterVisible !== 'boolean' ||
      !Array.isArray(clonedUIState.bookmarks.collapsedSection) ||
      !clonedUIState.searchAndFilterModal ||
      (typeof clonedUIState.dashboardModal.view !== 'string' &&
        clonedUIState.dashboardModal.view !== null) ||
      !clonedUIState.dashboardModal.files ||
      (!clonedUIState.characterFilter && clonedUIState.characterFilter !== null) ||
      (!clonedUIState.placeFilter && clonedUIState.placeFilter !== null)
    )
  }

  return !!(
    data &&
    (data.collaborators.some(shouldAddMissingStateToClonedUiState) ||
      data.viewers.some(shouldAddMissingStateToClonedUiState))
  )
}

/**
 * @param {PlottrFile_2026_4_13['ui']['collaborators']} collaborators
 * @return {PlottrFile_2026_7_4['ui']['collaborators']}
 */
function addMissingStateToCollaborators(collaborators) {
  /**
   * @param {CollaboratorUIState_2026_4_13} collaboratorUI
   * @return {CollaboratorUIState_2026_7_4}
   */
  const addMissingStateToCollaboratorUI = (collaboratorUI) => {
    return {
      ...collaboratorUI,
      noteTab: {
        ...collaboratorUI.noteTab,
        showAsList:
          typeof collaboratorUI.noteTab.showAsList !== 'boolean'
            ? true
            : collaboratorUI.noteTab.showAsList,
        collapsedCategories:
          typeof collaboratorUI.noteTab.collapsedCategories !== 'boolean'
            ? false
            : collaboratorUI.noteTab.collapsedCategories,
      },
      characterTab: {
        ...collaboratorUI.characterTab,
        showAsList:
          typeof collaboratorUI.characterTab.showAsList !== 'boolean'
            ? true
            : collaboratorUI.characterTab.showAsList,
        collapsedCategories:
          typeof collaboratorUI.characterTab.collapsedCategories !== 'boolean'
            ? false
            : collaboratorUI.characterTab.collapsedCategories,
      },
      placeTab: {
        ...collaboratorUI.placeTab,
        showAsList:
          typeof collaboratorUI.placeTab.showAsList !== 'boolean'
            ? true
            : collaboratorUI.placeTab.showAsList,
        collapsedCategories:
          typeof collaboratorUI.placeTab.collapsedCategories !== 'boolean'
            ? false
            : collaboratorUI.placeTab.collapsedCategories,
      },
      theme: !collaboratorUI.theme ? 'light-blue' : collaboratorUI.theme,
      mobileControlsModal: !collaboratorUI.mobileControlsModal
        ? {
            open: false,
          }
        : collaboratorUI.mobileControlsModal,
      outlineTab: {
        ...collaboratorUI.outlineTab,
        layout: {
          ...collaboratorUI.outlineTab.layout,
          fulltext: {
            ...collaboratorUI.outlineTab.layout.fulltext,
            hoverDetails: !collaboratorUI.outlineTab.layout.fulltext.hoverDetails
              ? {
                  lineTitle: null,
                  beatTitle: null,
                  cardTitle: null,
                  cardId: null,
                  visible: false,
                }
              : collaboratorUI.outlineTab.layout.fulltext.hoverDetails,
          },
        },
        filterVisible:
          typeof collaboratorUI.outlineTab.filterVisible !== 'boolean'
            ? false
            : collaboratorUI.outlineTab.filterVisible,
      },
      timeline: {
        ...collaboratorUI.timeline,
        filterVisible:
          typeof collaboratorUI.timeline.filterVisible !== 'boolean'
            ? false
            : collaboratorUI.timeline.filterVisible,
      },
      bookmarks: {
        ...collaboratorUI.bookmarks,
        collapsedSection: !Array.isArray(collaboratorUI.bookmarks.collapsedSection)
          ? []
          : collaboratorUI.bookmarks.collapsedSection,
      },
      searchAndFilterModal: !collaboratorUI.searchAndFilterModal
        ? {
            hideOnType: true,
            coords: null,
            isFloating: false,
          }
        : {
            ...collaboratorUI.searchAndFilterModal,
            isFloating: false,
          },
      dashboardModal: {
        ...collaboratorUI.dashboardModal,
        view:
          typeof collaboratorUI.dashboardModal.view !== 'string' &&
          collaboratorUI.dashboardModal.view !== null
            ? null
            : collaboratorUI.dashboardModal.view,
        files: {
          view:
            typeof collaboratorUI.dashboardModal.files?.view !== 'string' &&
            collaboratorUI.dashboardModal.files?.view !== null
              ? null
              : collaboratorUI.dashboardModal.files.view,
          sort:
            typeof collaboratorUI.dashboardModal.files?.sort !== 'string'
              ? 'date~desc'
              : collaboratorUI.dashboardModal.files.sort,
        },
      },
      templateModal: {
        ...collaboratorUI.templateModal,
        open: false,
      },
      characterFilter:
        !collaboratorUI.characterFilter && collaboratorUI.characterFilter !== null
          ? null
          : collaboratorUI.characterFilter,
      placeFilter:
        !collaboratorUI.placeFilter && collaboratorUI.placeFilter !== null
          ? null
          : collaboratorUI.placeFilter,
      // NOTE(ed): if we were in the middle of an import elsewhere,
      // this is something we can safely discard in this state.
      importModal: {
        ...collaboratorUI.importModal,
        data: null,
        bookData: null,
      },
    }
  }

  if (collaborators === null) {
    return collaborators
  } else {
    return {
      collaborators: collaborators.collaborators.reduce((acc, next) => {
        return [...acc, addMissingStateToCollaboratorUI(next)]
      }, []),
      viewers: collaborators.viewers.reduce((acc, next) => {
        return [...acc, addMissingStateToCollaboratorUI(next)]
      }, []),
    }
  }
}

/**
 * @param {PlottrFile_2026_4_13} data
 * @return {PlottrFile_2026_7_4}
 */
function migrate(data) {
  const shouldAddOutlineTabFilterVisible = typeof data.ui?.outlineTab?.filterVisible === 'undefined'
  const shouldAddSearchAndFilter =
    typeof data.ui?.searchAndFilterModal === 'undefined' ||
    typeof data.ui?.searchAndFilterModal?.hideOnType === 'undefined' ||
    typeof data.ui?.searchAndFilterModal?.coords === 'undefined'

  const aBeatNeedsToBeMigrated = Object.values(data.beats).some((beatTree) => {
    return Object.values(beatTree.index).some((beat) => {
      return typeof beat.fromTemplateId === 'undefined'
    })
  })

  const aFamilyTreeShouldBeMigrated = Object.values(data.familyTrees ?? {}).some((familyTree) => {
    return (
      'relationshipConfiguration' in familyTree &&
      Object.values(familyTree.relationshipConfiguration).some((configuration) => {
        return !('lineStyle' in configuration)
      })
    )
  })

  const shouldAddDashboardModalFilesView =
    typeof data.ui?.dashboardModal === 'undefined' ||
    typeof data.ui?.dashboardModal?.files === 'undefined' ||
    typeof data.ui?.dashboardModal?.files?.view === 'undefined' ||
    typeof data.ui?.dashboardModal?.files?.sort === 'undefined'

  const shouldAddTemplateModalOpen =
    !data?.ui?.templateModal || !(data?.ui?.templateModal && 'open' in data.ui.templateModal)

  /**
   * @param {OldNotePlaceOrCharacter} entity
   * @return {boolean}
   */
  const hasACategoryIdAsANumberValue = (entity) => {
    return typeof entity.categoryId === 'number'
  }

  const aNoteNeedsToBeMigrated = data.notes.some(hasACategoryIdAsANumberValue)
  const aCharacterNeedsToBeMigrated = data.characters.some(hasACategoryIdAsANumberValue)
  const aPlaceNeedsToBeMigrated = data.places.some(hasACategoryIdAsANumberValue)

  const shouldAddMissingUIStateToUICollaborators = shouldAddMissingStateToCollaboratorsOrViewers(
    data.ui.collaborators
  )

  if (
    !shouldAddSearchAndFilter &&
    !shouldAddOutlineTabFilterVisible &&
    !aBeatNeedsToBeMigrated &&
    !aFamilyTreeShouldBeMigrated &&
    !shouldAddDashboardModalFilesView &&
    !shouldAddTemplateModalOpen &&
    !shouldAddMissingUIStateToUICollaborators &&
    !aNoteNeedsToBeMigrated &&
    !aCharacterNeedsToBeMigrated &&
    !aPlaceNeedsToBeMigrated
  ) {
    // NOTE(ed): the migration does not apply and this file *meets*
    // the requirements of the 2026.7.4 schema.
    //
    // @ts-ignore
    return data
  } else {
    /**
     * @type {PlottrFile_2026_4_13}
     */
    const newData = cloneDeep(data)

    /**
     * @param {PlottrFile_2026_4_13['ui']} ui
     * @return {PlottrFile_2026_7_4['ui']}
     */
    const migrateUI = (ui) => {
      return {
        ...ui,
        collaborators: addMissingStateToCollaborators(ui.collaborators),
        noteTab: {
          ...ui.noteTab,
          showAsList: typeof ui.noteTab.showAsList !== 'boolean' ? true : ui.noteTab.showAsList,
          collapsedCategories:
            typeof ui.noteTab.collapsedCategories !== 'boolean'
              ? false
              : ui.noteTab.collapsedCategories,
        },
        characterTab: {
          ...ui.characterTab,
          showAsList:
            typeof ui.characterTab.showAsList !== 'boolean' ? true : ui.characterTab.showAsList,
          collapsedCategories:
            typeof ui.characterTab.collapsedCategories !== 'boolean'
              ? false
              : ui.characterTab.collapsedCategories,
        },
        placeTab: {
          ...ui.placeTab,
          showAsList: typeof ui.placeTab.showAsList !== 'boolean' ? true : ui.placeTab.showAsList,
          collapsedCategories:
            typeof ui.placeTab.collapsedCategories !== 'boolean'
              ? false
              : ui.placeTab.collapsedCategories,
        },
        theme: !ui.theme ? 'light-blue' : ui.theme,
        mobileControlsModal: !ui.mobileControlsModal
          ? {
              open: false,
            }
          : ui.mobileControlsModal,
        outlineTab: {
          ...ui.outlineTab,
          layout: {
            ...ui.outlineTab.layout,
            fulltext: {
              ...ui.outlineTab.layout.fulltext,
              hoverDetails: !ui.outlineTab.layout.fulltext.hoverDetails
                ? {
                    lineTitle: null,
                    beatTitle: null,
                    cardTitle: null,
                    cardId: null,
                    visible: false,
                  }
                : ui.outlineTab.layout.fulltext.hoverDetails,
            },
          },
          filterVisible:
            typeof ui.outlineTab.filterVisible !== 'boolean' ? false : ui.outlineTab.filterVisible,
        },
        timeline: {
          ...ui.timeline,
          filterVisible:
            typeof ui.timeline.filterVisible !== 'boolean' ? false : ui.timeline.filterVisible,
        },
        bookmarks: {
          ...ui.bookmarks,
          collapsedSection: !Array.isArray(ui.bookmarks.collapsedSection)
            ? []
            : ui.bookmarks.collapsedSection,
        },
        searchAndFilterModal: !ui.searchAndFilterModal
          ? {
              hideOnType: true,
              coords: null,
              isFloating: false,
            }
          : {
              ...ui.searchAndFilterModal,
              isFloating: false,
            },
        templateModal: {
          ...ui.templateModal,
          open: false,
        },
        dashboardModal: {
          ...ui.dashboardModal,
          view:
            typeof ui.dashboardModal.view !== 'string' && ui.dashboardModal.view !== null
              ? null
              : ui.dashboardModal.view,
          files: {
            view:
              typeof ui.dashboardModal.files?.view !== 'string' &&
              ui.dashboardModal.files?.view !== null
                ? null
                : ui.dashboardModal.files.view,
            sort:
              typeof ui.dashboardModal.files?.sort !== 'string'
                ? 'date~desc'
                : ui.dashboardModal.files.sort,
          },
        },
        characterFilter:
          !ui.characterFilter && ui.characterFilter !== null ? null : ui.characterFilter,
        placeFilter: !ui.placeFilter && ui.placeFilter !== null ? null : ui.placeFilter,
        // NOTE(ed): if we were in the middle of an import elsewhere,
        // this is something we can safely discard in this state.
        importModal: {
          ...ui.importModal,
          data: null,
          bookData: null,
        },
      }
    }

    /**
     * @param {PlottrFile_2026_4_13['beats']} beats
     * @return {PlottrFile_2026_7_4['beats']}
     */
    const migrateBeats = (beats) => {
      return Object.entries(beats).reduce((acc, next) => {
        const [key, beatTree] = next
        return {
          ...acc,
          [key]: {
            ...beatTree,
            index: Object.entries(beatTree.index).reduce((indexAcc, indexNext) => {
              const [indexKey, beat] = indexNext
              return {
                ...indexAcc,
                [indexKey]: {
                  ...beat,
                  fromTemplateId:
                    typeof beat.fromTemplateId === 'undefined' ? null : beat.fromTemplateId,
                },
              }
            }, {}),
          },
        }
      }, {})
    }

    /**
     * @param {PlottrFile_2026_4_13['familyTrees']} familyTrees
     * @return {PlottrFile_2026_7_4['familyTrees']}
     */
    const migrateFamilyTrees = (familyTrees) => {
      return Object.entries(familyTrees).reduce((acc, next) => {
        const [key, familyTree] = next
        return {
          ...acc,
          [key]: {
            ...familyTree,
            relationshipConfiguration: Object.entries(familyTree.relationshipConfiguration).reduce(
              (acc, next) => {
                const [key, relationshipConfiguration] = next
                return {
                  ...acc,
                  [key]: {
                    ...relationshipConfiguration,
                    lineStyle:
                      typeof relationshipConfiguration.lineStyle === 'undefined'
                        ? 'SOLID'
                        : relationshipConfiguration.lineStyle,
                  },
                }
              },
              {}
            ),
          },
        }
      }, {})
    }

    return {
      ...newData,
      ui: migrateUI(newData.ui),
      beats: migrateBeats(newData.beats),
      familyTrees: migrateFamilyTrees(newData.familyTrees),
      notes: newData.notes.map((note) => {
        return {
          ...note,
          categoryId:
            typeof note.categoryId === 'string' || note.categoryId === null
              ? note.categoryId
              : note.categoryId.toString(),
        }
      }),
      characters: newData.characters.map((character) => {
        return {
          ...character,
          categoryId:
            typeof character.categoryId === 'string' || character.categoryId === null
              ? character.categoryId
              : character.categoryId.toString(),
        }
      }),
      places: newData.places.map((place) => {
        return {
          ...place,
          categoryId:
            typeof place.categoryId === 'string' || place.categoryId === null
              ? place.categoryId
              : place.categoryId.toString(),
        }
      }),
    }
  }
}

export default migrate
